import React, { useState, useEffect } from 'react';
import { Menu, X, Languages } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_da9e6102-b17b-4e3a-82a5-102f912074e7/artifacts/esnq4twv_S2R_logo_750x750.png"
              alt="S2R Logo"
              className="h-12 w-auto"
            />
            <span className="text-xl font-bold text-black">SongsToRepresent</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('hem')}
              className="text-gray-700 hover:text-black transition-colors duration-200 font-medium"
            >
              {t('header.home')}
            </button>
            <button
              onClick={() => scrollToSection('om-oss')}
              className="text-gray-700 hover:text-black transition-colors duration-200 font-medium"
            >
              {t('header.about')}
            </button>
            <button
              onClick={() => scrollToSection('exempel')}
              className="text-gray-700 hover:text-black transition-colors duration-200 font-medium"
            >
              {t('header.examples')}
            </button>
            <button
              onClick={() => scrollToSection('pris')}
              className="text-gray-700 hover:text-black transition-colors duration-200 font-medium"
            >
              Pris
            </button>
            <button
              onClick={() => scrollToSection('kontakt')}
              className="text-gray-700 hover:text-black transition-colors duration-200 font-medium"
            >
              {t('header.contact')}
            </button>
            
            {/* Language Switcher */}
            <div className="relative group">
              <button className="flex items-center space-x-1 text-gray-700 hover:text-black transition-colors duration-200 font-medium">
                <Languages className="w-5 h-5" />
                <span className="uppercase text-sm">{i18n.language}</span>
              </button>
              <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <button
                  onClick={() => i18n.changeLanguage('sv')}
                  className={`block w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors ${i18n.language === 'sv' ? 'bg-gray-50 font-semibold' : ''}`}
                >
                  Svenska
                </button>
                <button
                  onClick={() => i18n.changeLanguage('en')}
                  className={`block w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors ${i18n.language === 'en' ? 'bg-gray-50 font-semibold' : ''}`}
                >
                  English
                </button>
              </div>
            </div>
            
            <Button
              onClick={() => scrollToSection('kontakt')}
              className="bg-black text-white hover:bg-gray-800 transition-colors duration-200"
            >
              {t('header.getStarted')}
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-black"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 flex flex-col space-y-4">
            <button
              onClick={() => scrollToSection('hem')}
              className="text-gray-700 hover:text-black transition-colors duration-200 font-medium text-left"
            >
              {t('header.home')}
            </button>
            <button
              onClick={() => scrollToSection('om-oss')}
              className="text-gray-700 hover:text-black transition-colors duration-200 font-medium text-left"
            >
              {t('header.about')}
            </button>
            <button
              onClick={() => scrollToSection('exempel')}
              className="text-gray-700 hover:text-black transition-colors duration-200 font-medium text-left"
            >
              {t('header.examples')}
            </button>
            <button
              onClick={() => scrollToSection('pris')}
              className="text-gray-700 hover:text-black transition-colors duration-200 font-medium text-left"
            >
              Pris
            </button>
            <button
              onClick={() => scrollToSection('kontakt')}
              className="text-gray-700 hover:text-black transition-colors duration-200 font-medium text-left"
            >
              {t('header.contact')}
            </button>
            
            {/* Mobile Language Switcher */}
            <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
              <Languages className="w-5 h-5 text-gray-700" />
              <button
                onClick={() => i18n.changeLanguage('sv')}
                className={`px-3 py-1 rounded ${i18n.language === 'sv' ? 'bg-black text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                SV
              </button>
              <button
                onClick={() => i18n.changeLanguage('en')}
                className={`px-3 py-1 rounded ${i18n.language === 'en' ? 'bg-black text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                EN
              </button>
            </div>
            
            <Button
              onClick={() => scrollToSection('kontakt')}
              className="bg-black text-white hover:bg-gray-800 transition-colors duration-200 w-full"
            >
              {t('header.getStarted')}
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;